/**
 * Search-engine metadata for the Security Center: one definition, read by
 * the live pages (react-helmet, through HelpSeo) and by the build-time
 * pre-renderer, so the HTML Google fetches and the page a visitor sees agree.
 * The tags themselves come from the Help Center's headTags.
 */
import { SECURITY_BASE_PATH, SECURITY_SITE_ORIGIN as ORIGIN } from './index.js';

const SITE_NAME = 'Defera Security';
const LOGO = `${ORIGIN}/defera-transparent.png`;
const ORG = { '@type': 'Organization', name: 'Defera', url: ORIGIN, logo: { '@type': 'ImageObject', url: LOGO } };

const crumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: `${ORIGIN}${it.path}` })),
});

export function securityHomeMeta(center) {
  return {
    title: 'Security at Defera: how we protect you',
    description: 'How Defera protects your account, payments and data, what we’ll never ask you for, and how to report a scam or a security vulnerability.',
    canonical: `${ORIGIN}${SECURITY_BASE_PATH}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Security at Defera',
        url: `${ORIGIN}${SECURITY_BASE_PATH}`,
        publisher: ORG,
        hasPart: center.pages.map((p) => ({ '@type': 'WebPage', name: p.title, url: `${ORIGIN}${p.path}` })),
      },
    ],
  };
}

export function securityPageMeta(page) {
  return {
    title: `${page.title} | ${SITE_NAME}`,
    description: page.summary,
    canonical: `${ORIGIN}${page.path}`,
    type: 'article',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: page.title,
        description: page.summary,
        dateModified: page.updated,
        author: ORG,
        publisher: ORG,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${ORIGIN}${page.path}` },
        articleSection: 'Security',
      },
      crumb([
        { name: 'Security', path: SECURITY_BASE_PATH },
        { name: page.title, path: page.path },
      ]),
    ],
  };
}

/** Every Security Center URL, for the sitemap. */
export function securityUrls(center) {
  return [
    { path: SECURITY_BASE_PATH, lastmod: center.updated },
    ...center.pages.map((p) => ({ path: p.path, lastmod: p.updated })),
  ];
}
